/**
 * @fileoverview Componente responsável por estruturar o layout autenticado, sidebar, navegação mobile e listeners globais.
 */

import React, { useState, useContext, useEffect } from "react";
import {
  makeStyles,
  Drawer,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";

import MainListItems from "./MainListItems";
import UserModal from "../components/UserModal";
import BottomNavigation from "../components/BottomNavigation";
import FaviconUnreadBadge from "../components/FaviconUnreadBadge";
import NoResponseAlertToast from "../components/NoResponseAlertToast";
import TriageNoticeToast from "../components/TriageNoticeToast";
import ImpersonationBanner from "../components/ImpersonationBanner";
import MessageUsageBanner from "../components/MessageUsageBanner";
import EmailVerificationBanner from "../components/EmailVerificationBanner";
import PlanLimitPrompt from "../components/Billing/PlanLimitPrompt";
import RestrictedAccessGate from "../components/Billing/RestrictedAccessGate";
import BillingStatusBanner from "../components/Billing/BillingStatusBanner";
import { useBilling } from "../context/Billing/BillingContext";
import useInternalChatNotifications from "../hooks/useInternalChatNotifications";
import useInternalChatAppBadge from "../hooks/useInternalChatAppBadge";
import usePermission, { isAdminUser } from "../hooks/usePermission";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";

import { SocketContext } from "../context/Socket/SocketContext";

import toastError from "../errors/toastError";

import ColorModeContext from "./themeContext";

const drawerWidth = 72; // Sidebar compacta

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    backgroundColor: theme.palette.fancyBackground,
    '& .MuiButton-outlinedPrimary': {
      color: theme.mode === 'light' ? '#FFF' : '#FFF',
      backgroundColor: theme.mode === 'light' ? theme.palette.primary.main : '#1A1A1A',
    },
    '& .MuiTab-textColorPrimary.Mui-selected': {
      color: theme.mode === 'light' ? 'Primary' : '#88898D',
    }
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    background: theme.palette.topBar,
    borderRight: theme.palette.type === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.06)'
      : '1px solid rgba(0, 0, 0, 0.08)',
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      display: "none", // Esconder drawer no mobile
    },
    overflowX: "hidden",
    ...theme.scrollbarStylesSoft
  },
  content: {
    flex: 1,
    overflow: "hidden",
    height: "auto",
    display: "flex",
    flexDirection: "column",
    position: "relative",
  },
  menuList: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
  },
}));

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const { user } = useContext(AuthContext);
  // O consumo do mês, o atraso e o aviso do limite com os planos são de quem cuida
  // da cobrança (billing.manage), com a regra nova ligada; sem ela, do admin e do
  // super. Decide desde o início pela regra de hoje.
  const { can } = usePermission();
  const managesBilling = can("billing.manage", isAdminUser(user));

  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [volume, setVolume] = useState(() => {
    const storedVolume = parseFloat(localStorage.getItem("volume"));
    return Number.isNaN(storedVolume) ? 1 : storedVolume;
  });

  const socketManager = useContext(SocketContext);

  // O BillingProvider vive no topo do App (fora do Router) e lê a cobrança ao
  // abrir. Depois do login (history.push, SPA, sem recarregar), relê quando o
  // layout com login monta e quando a conta ativa muda.
  const { refetchBilling } = useBilling();
  const activeCompanyId = user?.companyId;
  useEffect(() => {
    if (activeCompanyId) refetchBilling();
  }, [refetchBilling, activeCompanyId]);

  // Notificações nativas do browser para DMs e menções do Chat Interno.
  // O hook é global (vive no layout) pra funcionar mesmo quando o user não
  // está em /internal-chat. Canais comuns não disparam popup — só badge.
  useInternalChatNotifications();

  // Badge no ícone do PWA (igual WhatsApp) com o nº de DMs/menções não lidas.
  useInternalChatAppBadge();

  // States for BottomNavigation (mobile)
  const [connectionWarning, setConnectionWarning] = useState(false);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const userId = localStorage.getItem("userId");

    const socket = socketManager.getSocket(companyId);

    const handleAuth = (data) => {
      if (data.user.id === +userId) {
        toastError("Sua conta foi acessada em outro computador.");
        setTimeout(() => {
          localStorage.clear();
          window.location.reload();
        }, 1000);
      }
    };

    socket.on(`company-${companyId}-auth`, handleAuth);

    socket.emit("userStatus");
    const interval = setInterval(() => {
      socket.emit("userStatus");
    }, 1000 * 60 * 5);

    return () => {
      socket.off(`company-${companyId}-auth`, handleAuth);
      clearInterval(interval);
    };
  }, [socketManager]);

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
  };

  const handleClickLogout = () => {
    handleLogout();
  };

  const drawerClose = () => {
    // No-op - drawer is always collapsed
  };

  const toggleColorMode = () => {
    colorMode.toggleColorMode();
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div className={classes.root}>
      <FaviconUnreadBadge />
      <NoResponseAlertToast />
      <TriageNoticeToast />

      {/* O limite do plano (Empresas nos planos novos): explica e, no Starter, oferece os planos. */}
      <PlanLimitPrompt managesBilling={managesBilling} />

      {/* Desktop Sidebar */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          classes={{
            paper: classes.drawerPaper,
          }}
        >
          <div className={classes.menuList}>
            <MainListItems
              drawerClose={drawerClose}
              collapsed={true}
              user={user}
              volume={volume}
              setVolume={setVolume}
              toggleColorMode={toggleColorMode}
              onOpenProfile={handleOpenUserModal}
              onLogout={handleClickLogout}
            />
          </div>
        </Drawer>
      )}

      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />

      <main className={classes.content}>
        <ImpersonationBanner />
        {/* O consumo das mensagens do mês: só quem cuida do plano vê. */}
        {managesBilling && <MessageUsageBanner />}
        {/* O atraso no pagamento (etapa 10): a fatura vencida para quem cuida da cobrança; a suspensão para todos. */}
        <BillingStatusBanner managesBilling={managesBilling} isSuper={!!user?.super} />
        {/* E-mail ainda não confirmado: as operações sensíveis esperam a confirmação. */}
        <EmailVerificationBanner />
        {/* Acesso restrito (etapa 9 dos planos): até a escolha do que fica, só Faturamento. */}
        <RestrictedAccessGate managesBilling={managesBilling} isSuper={!!user?.super}>
          {children ? children : null}
        </RestrictedAccessGate>
      </main>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <BottomNavigation
          user={user}
          volume={volume}
          setVolume={setVolume}
          toggleColorMode={toggleColorMode}
          onOpenProfile={handleOpenUserModal}
          onLogout={handleClickLogout}
          connectionWarning={connectionWarning}
        />
      )}
    </div>
  );
};

export default LoggedInLayout;
